import React, { useState } from 'react';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const Contact = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    jobTitle: '',
    companySize: '',
    interest: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const companySizeOptions = [
    t('contact.companySizeOptions.opt1'),
    t('contact.companySizeOptions.opt2'),
    t('contact.companySizeOptions.opt3'),
    t('contact.companySizeOptions.opt4'),
    t('contact.companySizeOptions.opt5')
  ];

  const interestOptions = [
    t('contact.interestOptions.opt1'),
    t('contact.interestOptions.opt2'),
    t('contact.interestOptions.opt3'),
    t('contact.interestOptions.opt4')
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Submit to contact endpoint
      const response = await fetch(apiUrl("contact"), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      // Also submit to demo-bookings if interest indicates demo request
      const demoKeywords = ['demo', 'Demo', 'DEMO', 'Platform Demo', 'Platform Demosu'];
      const isDemo = demoKeywords.some(keyword => formData.interest?.includes(keyword));
      
      if (isDemo) {
        await fetch(apiUrl("demo-bookings"), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: formData.fullName,
            email: formData.email,
            company: formData.company,
            jobTitle: formData.jobTitle,
            companySize: formData.companySize,
            message: formData.message
          })
        });
      }
      
      if (response.ok && data.success) {
        toast.success(t('contact.form.success'));
        setSubmitted(true);
        
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            fullName: '',
            email: '',
            company: '',
            jobTitle: '',
            companySize: '',
            interest: '',
            message: ''
          });
        }, 5000);
      } else {
        toast.error(data.detail || t('contact.form.error'));
      }
    } catch (error) {
      logger.error('Error submitting form:', error);
      toast.error(t('contact.form.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Side - Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                  {t('contact.title')}
                </h2>
                <p className="text-xl text-gray-300">
                  {t('contact.subtitle')}
                </p>
              </div>
              
              {/* Contact Benefits */}
              <div className="space-y-6">
                {[
                  { key: 'quickResponse', title: t('contact.benefits.quickResponse.title'), desc: t('contact.benefits.quickResponse.description') },
                  { key: 'personalizedDemo', title: t('contact.benefits.personalizedDemo.title'), desc: t('contact.benefits.personalizedDemo.description') },
                  { key: 'expertGuidance', title: t('contact.benefits.expertGuidance.title'), desc: t('contact.benefits.expertGuidance.description') },
                  { key: 'noCommitment', title: t('contact.benefits.noCommitment.title'), desc: t('contact.benefits.noCommitment.description') }
                ].map((item, index) => {
                  const colors = ['text-blue-400', 'text-blue-300', 'text-blue-500', 'text-cyan-400'];
                  return (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center ${colors[index]}`}>
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Image */}
              <div className="hidden lg:block">
                <img 
                  src="https://images.unsplash.com/photo-1531973576160-7125cd663d86"
                  alt="Modern Office"
                  className="rounded-2xl shadow-xl w-full h-64 object-cover border-2 border-blue-500/30"
                />
              </div>
            </div>
            
            {/* Right Side - Form */}
            <div className="bg-gradient-to-br from-gray-800 to-slate-900 p-8 rounded-2xl shadow-xl border-2 border-gray-700">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-900/50 text-blue-400 mb-6">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {t('contact.form.success')}
                  </h3>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <Label htmlFor="fullName" className="text-gray-300">{t('contact.form.fullName')} *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="mt-2 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <Label htmlFor="email" className="text-gray-300">{t('contact.form.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-2 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500"
                      placeholder="john@company.com"
                    />
                  </div>
                  
                  {/* Company and Job Title */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company" className="text-gray-300">{t('contact.form.company')} *</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        required
                        value={formData.company}
                        onChange={handleInputChange}
                        className="mt-2 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500"
                        placeholder="Company Inc"
                      />
                    </div>
                    <div>
                      <Label htmlFor="jobTitle" className="text-gray-300">{t('contact.form.jobTitle')}</Label>
                      <Input
                        id="jobTitle"
                        name="jobTitle"
                        type="text"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        className="mt-2 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500"
                        placeholder="VP of HR"
                      />
                    </div>
                  </div>
                  
                  {/* Company Size */}
                  <div>
                    <Label htmlFor="companySize" className="text-gray-300">{t('contact.form.companySize')} *</Label>
                    <Select onValueChange={(value) => handleSelectChange('companySize', value)} required>
                      <SelectTrigger className="mt-2 bg-gray-900/50 border-gray-600 text-white">
                        <SelectValue placeholder={t('contact.form.companySize')} />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {companySizeOptions.map((option, i) => (
                          <SelectItem key={option} value={option} className="text-white hover:bg-gray-700">{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Interest */}
                  <div>
                    <Label htmlFor="interest" className="text-gray-300">{t('contact.form.interest')} *</Label>
                    <Select onValueChange={(value) => handleSelectChange('interest', value)} required>
                      <SelectTrigger className="mt-2 bg-gray-900/50 border-gray-600 text-white">
                        <SelectValue placeholder={t('contact.form.interest')} />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {interestOptions.map((option, i) => (
                          <SelectItem key={option} value={option} className="text-white hover:bg-gray-700">{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-gray-300">{t('contact.form.message')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-2 bg-gray-900/50 border-gray-600 text-white placeholder:text-gray-500"
                      rows={4}
                      placeholder="..."
                    />
                  </div>
                  
                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {t('contact.form.sending')}
                      </>
                    ) : (
                      <>
                        {t('contact.form.submit')}
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
